import { useExternalQuestionnaireFilterChipsTranslations } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/FilterChips/i18n";
import { questionTypeToIconName } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionsTable/columns/answerCol";
import { useTags } from "@app/shared/components/TagListEditor/data";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { QuestionType, TagType } from "@generated/client/graphql";
import { useMemo, type FC, type PropsWithChildren } from "react";
import { useTemplateQuestionGroups } from "../../data";
import { useSupplierQuestionsHeaderTranslations } from "./i18n";

export const AnswerTypeFilterChip: FC<{
    value: QuestionType[];
    onValueChange: (value: QuestionType[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    const options = Object.values(QuestionType).map((questionType) => ({
        value: questionType,
        label: t(getQuestionTypeLabel(questionType)),
        icon: questionTypeToIconName[questionType],
    }));

    const handleTypeChange = (values: (string | null)[]) => {
        onValueChange(values.filter((id) => id !== null) as QuestionType[]);
    };

    return (
        <Dropdown
            closeOnValueChange={false}
            value={value || []}
            onValueChange={handleTypeChange}
            selectType="multi"
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.answer_type_filter)}
                options={options}
                valueIdentifierKey="value"
                valueDisplayKey="label"
            />

            <Dropdown.Content align="start" hasPortal>
                {options?.map((option) => (
                    <Dropdown.SelectableItem
                        key={option.value}
                        value={option.value}
                    >
                        <Dropdown.Checkbox />
                        <Dropdown.ItemIcon
                            name={option.icon}
                            className="!text-primary"
                        />
                        <Dropdown.ItemText>{t(option.label)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const TagFilterChip: FC<{
    value: string[];
    onValueChange: (value: string[]) => void;
}> = ({ value, onValueChange }) => {
    const { t, keys } = useSupplierQuestionsHeaderTranslations();
    const handleTagChange = (values: (string | null)[]) => {
        onValueChange(values.filter((id) => id !== null));
    };

    const { tags } = useTags(TagType.Questionnaire);
    const options = tags?.map((tag) => ({
        value: tag.id,
        label: tag.name,
    }));

    return (
        <Dropdown
            closeOnValueChange={false}
            value={value || []}
            onValueChange={handleTagChange}
            selectType="multi"
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.questionnaire_filter_label)}
                options={options}
                valueIdentifierKey="value"
                valueDisplayKey="label"
            />

            <Dropdown.Content align="start" hasPortal>
                {options?.map((option) => (
                    <Dropdown.SelectableItem
                        key={option.value}
                        value={option.value}
                    >
                        <Dropdown.Checkbox />
                        <Dropdown.ItemText>{t(option.label)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const QuestionGroupFilterChip: FC<{
    value: string[];
    onValueChange: (value: string[]) => void;
    locale: SupportedLocales;
}> = ({ value, onValueChange, locale }) => {
    const { templateQuestionGroups } = useTemplateQuestionGroups();

    const { t, keys } = useSupplierQuestionsHeaderTranslations();

    const options = useMemo(() => {
        return templateQuestionGroups.map((group) => ({
            id: group.id,
            name: group.name?.[locale] ?? t(keys.group_not_translated),
        }));
    }, [templateQuestionGroups, locale, t, keys]);

    const handleQuestionGroupChange = (values: (string | null)[]) => {
        onValueChange(values.filter((id) => id !== null));
    };

    return (
        <Dropdown
            closeOnValueChange={false}
            value={value || []}
            onValueChange={handleQuestionGroupChange}
            selectType="multi"
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.question_group_filter_label)}
                options={options}
                valueIdentifierKey="id"
                valueDisplayKey="name"
            />
            <Dropdown.Content align="start" hasPortal>
                {options?.map((option) => (
                    <Dropdown.SelectableItem key={option.id} value={option.id}>
                        <Dropdown.Checkbox />
                        <Dropdown.ItemText>{option.name}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const FilterChips: FC<PropsWithChildren> & {
    AnswerTypeFilterChip: typeof AnswerTypeFilterChip;
    TagFilterChip: typeof TagFilterChip;
    QuestionGroupFilterChip: typeof QuestionGroupFilterChip;
} = ({ children }) => {
    const { t, keys } = useExternalQuestionnaireFilterChipsTranslations();
    return (
        <FlexRow wrap="wrap" gap="2" w="full" alignItems="center">
            <Text variant="caption-bold">{t(keys.label)}</Text>
            {children}
        </FlexRow>
    );
};

FilterChips.AnswerTypeFilterChip = AnswerTypeFilterChip;
FilterChips.TagFilterChip = TagFilterChip;
FilterChips.QuestionGroupFilterChip = QuestionGroupFilterChip;

function getQuestionTypeLabel(questionType: QuestionType): string {
    if (questionType === QuestionType.SingleSelect) {
        return "question_type_single_select";
    }
    if (questionType === QuestionType.MultiSelect) {
        return "question_type_multi_select";
    }
    if (questionType === QuestionType.Text) {
        return "question_type_text";
    }
    return "question_type_number";
}
